/**
 * React Query hooks for agent management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createAgent, deleteAgent, getAgentVersion, listAgents, updateAgent } from '@/api/agents';
import type { AgentCreateRequest, AgentUpdateRequest } from '@/types/agent';

const AGENTS_KEY = ['agents'] as const;

export function useAgents(options: { refetchInterval?: number | false } = {}) {
  return useQuery({
    queryKey: AGENTS_KEY,
    queryFn: listAgents,
    refetchInterval: options.refetchInterval ?? false,
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentCreateRequest) => createAgent(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_KEY });
    },
  });
}

export function useUpdateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, payload }: { agentId: string; payload: AgentUpdateRequest }) =>
      updateAgent(agentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_KEY });
    },
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => deleteAgent(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_KEY });
    },
  });
}

export function useAgentVersion() {
  return useQuery({
    queryKey: ['agent-version'],
    queryFn: getAgentVersion,
    staleTime: 5 * 60 * 1000, // refetch at most every 5 minutes
  });
}
